import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  Node,
  Edge,
  Connection,
  addEdge,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import PageHeader from '../components/PageHeader';
import WorkflowEditModal from '../components/WorkflowEditModal';
import { getWorkflowById, updateWorkflow, WORKFLOW_ITEM_TYPE_OPTIONS, normalizeWorkflowItemType } from '../services/workflowService';
import { GEMINI_MODELS, GeminiModelName } from '../config/constants';

const nodeDefaults = {
  sourcePosition: Position.Right,
  targetPosition: Position.Left,
};

// Helper function to truncate prompt text
const truncatePrompt = (prompt: string, maxLength: number = 50): string => {
  if (!prompt || prompt.length <= maxLength) return prompt;
  return prompt.substring(0, maxLength) + '...';
};

// Helper function to get handle color based on type
const getHandleColor = (type: string) => {
  if (type === 'image[]') return '#3b82f6'; // Blue
  if (type === 'string') return '#22c55e'; // Green
  if (type === 'number') return '#eab308'; // Yellow
  return '#6b7280'; // Gray default
};

// Custom node component for Triage Outcome (no input handle)
const InputNode = ({ data }: any) => {
  const outputs = data.outputs || [];
  const headerOffset = 21;
  const sizeBias = 1.05;
  
  return (
    <div className="relative px-4 py-3 shadow-lg rounded-lg bg-blue-50 dark:bg-blue-900 border-2 border-blue-500 dark:border-blue-400 min-w-[200px]">
      {/* Execution output at the top */}
      <Handle
        type="source"
        position={Position.Right}
        id="execution"
        style={{ 
          top: `25px`,
          background: '#f59e0b',
          width: '12px',
          height: '12px',
          border: '2px solid #d97706',
        }}
      />
      
      <div className="font-bold text-blue-900 dark:text-blue-100 mb-2">{data.label}</div>
      <div className="text-xs text-blue-700 dark:text-blue-200 space-y-1">
        {outputs.map((output: any) => (
          <div key={output.id} className="py-1 text-right">
            {output.label}: <span className="font-mono">{output.type}</span>
          </div>
        ))}
      </div>

      {/* Output handles positioned on the right side */}
      {outputs.map((output: any, idx: number) => (
        <Handle
          key={output.id}
          type="source"
          position={Position.Right}
          id={output.id}
          style={{ 
            top: `${((idx + 1) * (100 - headerOffset)) / (outputs.length + 1) * sizeBias + headerOffset}%`,
            background: getHandleColor(output.type),
            width: '10px',
            height: '10px',
          }}
        />
      ))}
    </div>
  );
};

// Custom node component for Branch Node
const BranchNode = ({ data, id }: any) => {
  const [localData, setLocalData] = useState(data);
  
  // Update local data when parent data changes
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const handleLabelChange = (newLabel: string) => {
    const updatedData = { ...localData, label: newLabel };
    setLocalData(updatedData);
    // Trigger parent update
    if (data.onUpdate) {
      data.onUpdate(id, updatedData);
    }
  };
  
  return (
    <div className="relative px-4 py-3 shadow-lg rounded-lg bg-purple-50 dark:bg-purple-900 border-2 border-purple-500 dark:border-purple-400 min-w-[220px]">
      {/* Execution input at the top left */}
      <Handle
        type="target"
        position={Position.Left}
        id="entry-point"
        style={{ 
          top: `25px`,
          background: '#f59e0b',
          width: '12px',
          height: '12px',
          border: '2px solid #d97706',
        }}
      />
      
      <div className="font-bold text-purple-900 dark:text-purple-100 mb-3 text-center">
        Branch
      </div>
      
      <div className="text-xs text-purple-700 dark:text-purple-200 space-y-2">
        {/* Editable Label */}
        <div>
          <label className="font-semibold block mb-1">Condition Label:</label>
          <input
            type="text"
            value={localData.label || ''}
            onChange={(e) => handleLabelChange(e.target.value)}
            placeholder="e.g., Quality Check"
            className="w-full px-2 py-1 text-xs bg-white dark:bg-purple-800 border border-purple-300 dark:border-purple-600 rounded text-purple-900 dark:text-purple-100"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
      
      {/* True output (top right) */}
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        style={{ 
          top: `40px`,
          background: '#22c55e',
          width: '12px',
          height: '12px',
          border: '2px solid #16a34a',
        }}
      />
      
      {/* False output (bottom right) */}
      <Handle
        type="source"
        position={Position.Right}
        id="false"
        style={{ 
          top: `80px`,
          background: '#ef4444',
          width: '12px',
          height: '12px',
          border: '2px solid #dc2626',
        }}
      />
    </div>
  );
};

// Custom node component for Sequence Node
const SequenceNode = ({ data, id }: any) => {
  const [localData, setLocalData] = useState(data);
  
  // Update local data when parent data changes
  useEffect(() => {
      setLocalData(data);
  }, [data]);

  const outputs = localData.outputs || [{ id: 'out-1', label: 'Output 1' }];

  const handleAddOutput = () => {
      const newOutputId = `out-${outputs.length + 1}`;
      const newOutputs = [...outputs, { id: newOutputId, label: `Output ${outputs.length + 1}` }];
      const updatedData = { ...localData, outputs: newOutputs };
      setLocalData(updatedData);
      if (data.onUpdate) {
          data.onUpdate(id, updatedData);
      }
  };

  const handleRemoveOutput = (outputId: string) => {
      if (outputs.length <= 1) {
          alert('Sequence node must have at least one output');
          return;
      }
      const newOutputs = outputs.filter((o: any) => o.id !== outputId);
      const updatedData = { ...localData, outputs: newOutputs };
      setLocalData(updatedData);
      if (data.onUpdate) {
          data.onUpdate(id, updatedData);
      }
  };

  const handleOutputLabelChange = (outputId: string, newLabel: string) => {
      const newOutputs = outputs.map((o: any) => 
          o.id === outputId ? { ...o, label: newLabel } : o
      );
      const updatedData = { ...localData, outputs: newOutputs };
      setLocalData(updatedData);
      if (data.onUpdate) {
          data.onUpdate(id, updatedData);
      }
  };

  return (
    <div className="relative px-4 py-3 shadow-lg rounded-lg bg-blue-50 dark:bg-blue-900 border-2 border-blue-500 dark:border-blue-400 min-w-[240px]">
      {/* Execution input at the left */}
      <Handle
          type="target"
          position={Position.Left}
          id="entry-point"
          style={{ 
              top: `25px`,
              background: '#f59e0b',
              width: '12px',
              height: '12px',
              border: '2px solid #d97706',
          }}
      />
      
      <div className="font-bold text-blue-900 dark:text-blue-100 mb-3 text-center">
          Sequence
      </div>
      
      <div className="text-xs text-blue-700 dark:text-blue-200 space-y-2">
        {/* Dynamic Outputs */}
        {outputs.map((output: any, index: number) => {
          const topPosition = 60 + (index * 34.2);
          return (
            <div key={output.id} className="flex items-center gap-2">
              <input
                type="text"
                value={output.label}
                onChange={(e) => handleOutputLabelChange(output.id, e.target.value)}
                className="flex-1 px-2 py-1 text-xs bg-white dark:bg-blue-800 border border-blue-300 dark:border-blue-600 rounded text-blue-900 dark:text-blue-100"
                placeholder="Output label"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveOutput(output.id);
                }}
                className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                title="Remove output"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <Handle
                type="source"
                position={Position.Right}
                id={output.id}
                style={{ 
                  top: `${topPosition}px`,
                  background: '#f59e0b',
                  width: '12px',
                  height: '12px',
                  border: '2px solid #d97706',
                }}
              />
            </div>
          );
        })}
        
        {/* Add Output Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleAddOutput();
          }}
          className="w-full px-2 py-1 text-xs bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-300 dark:hover:bg-blue-700 transition-colors flex items-center justify-center gap-1 mt-2"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Output
        </button>
      </div>
    </div>
  );
};

// Custom node component for Api Checker Node
const ApiCheckerNode = ({ data, id }: any) => {
  const [localData, setLocalData] = useState(data);
  
  const platforms = [
      'WooCommerce',
      'eBay',
      'Delcampe',
      'Etsy',
      'Hipstamp',
      'Hippostcard'
  ];
  
  // Update local data when parent data changes
  useEffect(() => {
      setLocalData(data);
  }, [data]);

  const handlePlatformChange = (newPlatform: string) => {
      const updatedData = { ...localData, platform: newPlatform };
      setLocalData(updatedData);
      // Trigger parent update
      if (data.onUpdate) {
          data.onUpdate(id, updatedData);
      }
  };
  
  return (
    <div className="relative px-4 py-3 shadow-lg rounded-lg bg-purple-50 dark:bg-purple-900 border-2 border-purple-500 dark:border-purple-400 min-w-[220px]">
      {/* Execution input at the top left */}
      <Handle
        type="target"
        position={Position.Left}
        id="entry-point"
        style={{ 
          top: `25px`,
          background: '#f59e0b',
          width: '12px',
          height: '12px',
          border: '2px solid #d97706',
        }}
      />
      
      <div className="font-bold text-purple-900 dark:text-purple-100 mb-3 text-center">
        API Checker
      </div>
      
      <div className="text-xs text-purple-700 dark:text-purple-200 space-y-2">
        {/* Platform Dropdown */}
        <div>
          <label className="font-semibold block mb-1">Platform:</label>
          <select
            value={localData.platform || platforms[0]}
            onChange={(e) => handlePlatformChange(e.target.value)}
            className="w-full px-2 py-1 text-xs bg-white dark:bg-purple-800 border border-purple-300 dark:border-purple-600 rounded text-purple-900 dark:text-purple-100"
            onClick={(e) => e.stopPropagation()}
          >
            {platforms.map((platform) => (
              <option key={platform} value={platform}>
                {platform}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* True output (top right) */}
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        style={{ 
          top: `40px`,
          background: '#22c55e',
          width: '12px',
          height: '12px',
          border: '2px solid #16a34a',
        }}
      />
      
      {/* False output (bottom right) */}
      <Handle
        type="source"
        position={Position.Right}
        id="false"
        style={{ 
          top: `80px`,
          background: '#ef4444',
          width: '12px',
          height: '12px',
          border: '2px solid #dc2626',
        }}
      />
    </div>
  );
};

// Custom node component for Send to API Node
const SendToApiNode = ({ data, id }: any) => {
  const [localData, setLocalData] = useState(data);
  
  const platforms = [
    'WooCommerce',
    'eBay',
    'Delcampe',
    'Etsy',
    'Hipstamp',
    'Hippostcard'
  ];
  
  // Update local data when parent data changes
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const handlePlatformChange = (newPlatform: string) => {
    const updatedData = { ...localData, platform: newPlatform };
    setLocalData(updatedData);
    if (data.onUpdate) {
      data.onUpdate(id, updatedData);
    }
  };
  
  return (
    <div className="relative px-4 py-3 shadow-lg rounded-lg bg-yellow-50 dark:bg-yellow-900 border-2 border-yellow-500 dark:border-yellow-400 min-w-[220px]">
      {/* Execution input at the top left */}
      <Handle
        type="target"
        position={Position.Left}
        id="entry-point"
        style={{ 
          top: `25px`,
          background: '#f59e0b',
          width: '12px',
          height: '12px',
          border: '2px solid #d97706',
        }}
      />
      
      {/* Text input handle for AI evaluation */}
      <Handle
        type="target"
        position={Position.Left}
        id="ai-evaluation"
        style={{ 
          top: `65px`,
          background: '#22c55e',
          width: '10px',
          height: '10px',
        }}
      />
      
      <div className="font-bold text-yellow-900 dark:text-yellow-100 mb-3 text-center">
          Send to API
      </div>
      
      <div className="text-xs text-yellow-700 dark:text-yellow-200 space-y-2">
        {/* AI Evaluation Input Label */}
        <div className="text-xs mb-2">
          ← AI Evaluation
        </div>
        
        {/* Platform Dropdown */}
        <div>
          <label className="font-semibold block mb-1">Platform:</label>
          <select
            value={localData.platform || platforms[0]}
            onChange={(e) => handlePlatformChange(e.target.value)}
            className="w-full px-2 py-1 text-xs bg-white dark:bg-yellow-800 border border-yellow-300 dark:border-yellow-600 rounded text-yellow-900 dark:text-yellow-100"
            onClick={(e) => e.stopPropagation()}
          >
            {platforms.map((platform) => (
              <option key={platform} value={platform}>
                {platform}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// Custom node component for Prompt Node
const PromptNode = ({ data, id }: any) => {
  const [localData, setLocalData] = useState(data);
  
  // Update local data when parent data changes
  useEffect(() => {
    setLocalData(data);
  }, [data]);
  
  const inputs = data.inputs || [
    { id: 'text-in', label: 'Text', type: 'string' },
    { id: 'images-in', label: 'Images', type: 'image[]' }
  ];
  
  const outputs = data.outputs || [
    { id: 'text-out', label: 'Text', type: 'string' },
    { id: 'images-out', label: 'Images', type: 'image[]' }
  ];

  const handleModelChange = (newModel: string) => {
    const updatedData = { ...localData, model: newModel };
    setLocalData(updatedData);
    // Trigger parent update
    if (data.onUpdate) {
      data.onUpdate(id, updatedData);
    }
  };

  const handlePromptClick = () => {
    // Trigger parent to open modal
    if (data.onPromptClick) {
      data.onPromptClick(id, localData.prompt || '');
    }
  };
  
  return (
    <div className="relative px-4 py-3 shadow-lg rounded-lg bg-green-50 dark:bg-green-900 border-2 border-green-500 dark:border-green-400">
      {/* Entry point at the top */}
      <Handle
        type="target"
        position={Position.Left}
        id="entry-point"
        style={{ 
          top: `25px`,
          background: '#9ee698ff',
          width: '12px',
          height: '12px',
          border: '2px solid #d97706',
        }}
      />
      
      {/* Completed output at the bottom */}
      <Handle
        type="source"
        position={Position.Right}
        id="execution"
        style={{ 
          top: `25px`,
          background: '#f59e0b',
          width: '12px',
          height: '12px',
          border: '2px solid #d97706',
        }}
      />
      
      {/* Input handles on the left */}
      {inputs.map((input: any, idx: number) => (
        <Handle
          key={input.id}
          type="target"
          position={Position.Left}
          id={input.id}
          style={{ 
            top: `${(idx * 20) + 57}px`,
            background: getHandleColor(input.type),
            width: '10px',
            height: '10px',
          }}
        />
      ))}
      
      <div className="font-bold text-green-900 dark:text-green-100 mb-3 text-center">
        {localData.name || 'Prompt Node'}
      </div>
      
      <div className="text-xs text-green-700 dark:text-green-200 space-y-2">
        {/* Input/Output Grid */}
        <div className="grid grid-cols-2 gap-4 pb-2 border-b border-green-300 dark:border-green-700">
          {/* Inputs Column */}
          <div>
            {inputs.map((input: any) => (
              <div key={input.id} className="text-xs mb-1">
                ← {input.label}
              </div>
            ))}
          </div>
          {/* Outputs Column */}
          <div className="text-right">
            {outputs.map((output: any) => (
              <div key={output.id} className="text-xs mb-1">
                {output.label} →
              </div>
            ))}
          </div>
        </div>
        
        {/* Model Dropdown */}
        <div>
          <label className="font-semibold block mb-1">Model:</label>
          <select
            value={localData.model || 'gemini-2.5-pro'}
            onChange={(e) => handleModelChange(e.target.value)}
            className="w-full px-2 py-1 text-xs bg-white dark:bg-green-800 border border-green-300 dark:border-green-600 rounded text-green-900 dark:text-green-100"
            onClick={(e) => e.stopPropagation()}
          >
            {Object.keys(GEMINI_MODELS).map((model) => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
        </div>
        
        {/* Prompt Text */}
        <div>
          <label className="font-semibold block mb-1">Prompt:</label>
          <div 
            className="text-xs italic bg-white dark:bg-green-800 border border-green-300 dark:border-green-600 rounded px-2 py-1 min-h-[40px] cursor-pointer hover:border-green-500 dark:hover:border-green-300 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handlePromptClick();
            }}
            title={localData.prompt || 'Click to edit prompt...'}
          >
            {truncatePrompt(localData.prompt) || 'Click to edit prompt...'}
          </div>
        </div>
      </div>
      
      {/* Output handles on the right */}
      {outputs.map((output: any, idx: number) => (
        <Handle
          key={output.id}
          type="source"
          position={Position.Right}
          id={output.id}
          style={{ 
            top: `${(idx * 20 + 57)}px`,
            background: getHandleColor(output.type),
            width: '10px',
            height: '10px',
          }}
        />
      ))}
    </div>
  );
};

const WorkflowEditor: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [workflowId, setWorkflowId] = useState<number | null>(null);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowType, setWorkflowType] = useState<string>(WORKFLOW_ITEM_TYPE_OPTIONS[0]);
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [selectedEdges, setSelectedEdges] = useState<string[]>([]);
  const [promptModal, setPromptModal] = useState<{
    isOpen: boolean;
    nodeId: string | null;
    currentPrompt: string;
  }>({ isOpen: false, nodeId: null, currentPrompt: '' });

  // Load workflow data when component mounts
  useEffect(() => {
    const loadWorkflow = async () => {
      const params = new URLSearchParams(location.search);
      const id = params.get('id');
      
      if (id) {
        try {
          const workflow = await getWorkflowById(parseInt(id));
          setWorkflowId(workflow.id);
          setWorkflowName(workflow.name);
          setWorkflowType(normalizeWorkflowItemType(workflow.workflow_type));
          setWorkflowDescription(workflow.description);
          
          // Load nodes and edges if they exist in workflow_json
          if (workflow.workflow_json?.nodes && Array.isArray(workflow.workflow_json.nodes)) {
            setNodes(workflow.workflow_json.nodes);
          }
          if (workflow.workflow_json?.edges && Array.isArray(workflow.workflow_json.edges)) {
            setEdges(workflow.workflow_json.edges);
          }
        } catch (err) {
          console.error('Error loading workflow:', err);
          alert('Failed to load workflow');
        }
      }
      setIsLoading(false);
    };

    loadWorkflow();
  }, [location.search, setNodes, setEdges]);

  // Handle node data updates
  const handleNodeUpdate = useCallback((nodeId: string, newData: any) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: { ...node.data, ...newData }
          };
        }
        return node;
      })
    );
  }, [setNodes]);

  // Handle prompt modal
  const handlePromptClick = useCallback((nodeId: string, currentPrompt: string) => {
    setPromptModal({
      isOpen: true,
      nodeId,
      currentPrompt
    });
  }, []);

  const handlePromptSave = async (newPrompt: string) => {
    if (promptModal.nodeId) {
      handleNodeUpdate(promptModal.nodeId, { prompt: newPrompt });
    }
    setPromptModal({ isOpen: false, nodeId: null, currentPrompt: '' });
  };

  // Inject onUpdate handler into all nodes
  const nodesWithHandlers = useMemo(() => {
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        onUpdate: handleNodeUpdate,
        onPromptClick: handlePromptClick
      }
    }));
  }, [nodes, handleNodeUpdate, handlePromptClick]);

  const nodeTypes = useMemo(() => ({ 
    inputNode: InputNode,
    promptNode: PromptNode,
    branchNode: BranchNode,
    sequenceNode: SequenceNode,
    apiCheckerNode: ApiCheckerNode,
    sendToApiNode: SendToApiNode
  }), []);

  // Handle keyboard delete
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Delete' || event.key === 'Backspace') {
        // Check if we're not in an input field
        const target = event.target;
        if (
          target instanceof Element &&
          (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')
        ) {
          return;
        }

        // Delete selected edges
        if (selectedEdges.length > 0) {
          setEdges((eds) => eds.filter((e) => !selectedEdges.includes(e.id)));
          setSelectedEdges([]);
        }

        // Delete selected nodes (except triage nodes)
        if (selectedNodes.length > 0) {
          const nodesToDelete = nodes.filter(
            (n) => selectedNodes.includes(n.id) && n.type !== 'triageOutcome'
          );
          
          if (nodesToDelete.length > 0) {
            const nodeIdsToDelete = nodesToDelete.map((n) => n.id);
            
            // Remove nodes
            setNodes((nds) => nds.filter((n) => !nodeIdsToDelete.includes(n.id)));
            
            // Remove connected edges
            setEdges((eds) => eds.filter(
              (e) => !nodeIdsToDelete.includes(e.source) && !nodeIdsToDelete.includes(e.target)
            ));
              
              setSelectedNodes([]);
          } else if (selectedNodes.some((id) => nodes.find((n) => n.id === id)?.type === 'triageOutcome')) {
            // Show message if trying to delete triage node
            alert('The Triage Outcome node cannot be deleted.');
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodes, selectedEdges, nodes, setNodes, setEdges]);

  // Track node selection
  const onSelectionChange = useCallback(({ nodes: selectedNodes, edges: selectedEdges }: any) => {
    setSelectedNodes(selectedNodes.map((n: Node) => n.id));
    setSelectedEdges(selectedEdges.map((e: Edge) => e.id));
  }, []);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const isValidConnection = useCallback(
    (connection: Connection) => {
      const sourceNode = nodes.find((n) => n.id === connection.source);
      const targetNode = nodes.find((n) => n.id === connection.target);

      if (!sourceNode || !targetNode) return false;

      const sourceHandle = connection.sourceHandle;
      const targetHandle = connection.targetHandle;

      // Execution flow connections (orange handles)
      const executionHandles = ['execution', 'entry-point', 'completed', 'true', 'false'];
      const isSourceExecution = executionHandles.includes(sourceHandle || '') || 
        (sourceNode.type === 'sequenceNode' && sourceHandle?.startsWith('out-'));
      const isTargetExecution = executionHandles.includes(targetHandle || '');

      // If one is execution and the other isn't, reject
      if (isSourceExecution !== isTargetExecution) {
        return false;
      }

      // If both are execution handles, allow connection
      if (isSourceExecution && isTargetExecution) {
        return true;
      }

      // For data connections, check type matching
      const sourceOutput = sourceNode.data.outputs?.find((o: any) => o.id === sourceHandle);
      const targetInput = targetNode.data.inputs?.find((i: any) => i.id === targetHandle);

      // If we can't find the handle definitions, allow connection (for backwards compatibility)
      if (!sourceOutput || !targetInput) {
        return true;
      }

      // Check if types match
      return sourceOutput.type === targetInput.type;
    },
    [nodes]
  );

  const onEdgeUpdate = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      setEdges((els) => {
        // Remove the old edge and add the new connection
        const filtered = els.filter((e) => e.id !== oldEdge.id);
        return addEdge(newConnection, filtered);
      });
    },
    [setEdges]
  );

  const onEdgeUpdateStart = useCallback(() => {
    // Optional: You can add visual feedback here
  }, []);

  const onEdgeUpdateEnd = useCallback(
    (_: any, edge: Edge) => {
      // If the edge is dropped in empty space, remove it
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    },
    [setEdges]
  );

  const addPromptNode = useCallback(() => {
    const newNode = {
      id: `prompt-${Date.now()}`,
      type: 'promptNode',
      position: { x: 400, y: 100 },
      width: 1350,
      data: {
        name: 'New Prompt',
        model: 'gemini-2.5-pro',
        prompt: 'Enter your prompt here...',
        inputs: [
          { id: 'images-in', label: 'Images', type: 'image[]' },
          { id: 'text-in', label: 'Text', type: 'string' },
        ],
        outputs: [
          { id: 'images-out', label: 'Images', type: 'image[]' },
          { id: 'text-out', label: 'Text', type: 'string' },
        ]
      },
      deletable: true
    };
    
    setNodes((nds) => [...nds, newNode]);
  }, [setNodes]);

  const addBranchNode = useCallback(() => {
    const newNode = {
      id: `branch-${Date.now()}`,
      type: 'branchNode',
      position: { x: 400, y: 300 },
      data: {
        label: 'New Branch',
      },
      deletable: true
    };
    
    setNodes((nds) => [...nds, newNode]);
  }, [setNodes]);

  const addSequenceNode = useCallback(() => {
    const newNode = {
      id: `sequence-${Date.now()}`,
      type: 'sequenceNode',
      position: { x: 400, y: 400 },
      data: {
        outputs: [
          { id: 'out-1', label: 'Output 1' },
          { id: 'out-2', label: 'Output 2' }
        ],
        onUpdate: handleNodeUpdate
      },
      deletable: true
    };
    
    setNodes((nds) => [...nds, newNode]);
  }, [setNodes, handleNodeUpdate]);

  const addApiCheckerNode = useCallback(() => {
    const newNode = {
      id: `apichecker-${Date.now()}`,
      type: 'apiCheckerNode',
      position: { x: 400, y: 500 },
      data: {
        platform: 'WooCommerce',
        onUpdate: handleNodeUpdate
      },
      deletable: true
    };
    
    setNodes((nds) => [...nds, newNode]);
  }, [setNodes, handleNodeUpdate]);

  const addSendToApiNode = useCallback(() => {
    const newNode = {
      id: `sendtoapi-${Date.now()}`,
      type: 'sendToApiNode',
      position: { x: 400, y: 600 },
      data: {
        platform: 'WooCommerce',
        onUpdate: handleNodeUpdate
      },
      deletable: true
    };
    
    setNodes((nds) => [...nds, newNode]);
  }, [setNodes, handleNodeUpdate]);

  const handleSaveChanges = async () => {
    if (!workflowId) {
      alert('No workflow loaded');
      return;
    }

    try {
      setIsSaving(true);
      setSaveSuccess(false);

      await updateWorkflow(workflowId, {
        name: workflowName,
        workflow_type: workflowType,
        description: workflowDescription,
        workflow_json: {
          nodes: nodes,
          edges: edges
        }
      });

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving workflow:', err);
      const error = err;
      alert('Failed to save workflow: ' + ((error && error.response && error.response.data && error.response.data.message) || (error && error.message) || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <PageHeader />
      
      {/* Prompt Edit Modal - Outside React Flow */}
      <WorkflowEditModal
        isOpen={promptModal.isOpen}
        onClose={() => setPromptModal({ isOpen: false, nodeId: null, currentPrompt: '' })}
        onSave={handlePromptSave}
        title="Edit Prompt"
        initialValue={promptModal.currentPrompt}
        fieldName="prompt"
        isTextArea={true}
      />
      
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading workflow...</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Title */}
          <div className="mb-6">
            <div className="flex items-center justify-between gap-4">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Workflow Editor
              </h1>
              <button
                type="button"
                onClick={() => history.push('/workflow-management')}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
              >
                Back to Workflows
              </button>
            </div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Create and edit AI processing workflows with visual flow diagrams
            </p>
          </div>

          {/* Workflow Details */}
          <div className="card mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={workflowName}
                  onChange={(e) => setWorkflowName(e.target.value)}
                  placeholder="e.g., Stamp Analysis"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type
                </label>
                <select
                  value={workflowType}
                  onChange={(e) => setWorkflowType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {WORKFLOW_ITEM_TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                placeholder="Enter workflow description"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Node Controls */}
          <div className="mb-4 flex justify-between items-center">
            <div className="flex gap-2">
              <button
                onClick={addPromptNode}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Prompt Node
              </button>
              
              <button
                onClick={addBranchNode}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12M8 12h12M8 17h12M4 7h.01M4 12h.01M4 17h.01" />
                </svg>
                Add Branch Node
              </button>

              <button
                onClick={addSequenceNode}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                Add Sequence Node
              </button>

              <button
                onClick={addApiCheckerNode}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Add API Checker
              </button>

              <button
                onClick={addSendToApiNode}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Add Send to API
              </button>
            </div>
            
            <div className="flex items-center gap-3">
              {saveSuccess && (
                <span className="text-sm text-green-600 dark:text-green-400">
                  ✓ Workflow Saved
                </span>
              )}
              <button
                onClick={handleSaveChanges}
                disabled={isSaving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>

          {/* React Flow Graph */}
          <div className="card p-0 overflow-hidden" style={{ height: '800px', width: '100%' }}>
            <div style={{ width: '100%', height: '100%' }}>
              <ReactFlow
                nodes={nodesWithHandlers}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onEdgeUpdate={onEdgeUpdate}
                onEdgeUpdateStart={onEdgeUpdateStart}
                onEdgeUpdateEnd={onEdgeUpdateEnd}
                onSelectionChange={onSelectionChange}
                isValidConnection={isValidConnection}
                nodeTypes={nodeTypes}
                defaultEdgeOptions={{ animated: true }}
                defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
                fitView
                nodesDraggable={true}
                nodesConnectable={true}
                elementsSelectable={true}
                proOptions={{ hideAttribution: true }}
              >
                <Controls />
                <MiniMap />
                <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
              </ReactFlow>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowEditor;
